import { formatPatientName, getHospitalNameForCode } from '@billing/validation';
import {
  Accordion,
  Alert,
  Button,
  Card,
  Divider,
  Flex,
  Stack,
  Text,
  TextInput,
  useComputedColorScheme,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { Claim } from 'interfaces';
import { formatDateTime, parseDateTime } from 'utils/dateUtils';

interface ClaimInvoicePreviewProps {
  claim: Claim | null;
  selectedTariffIDs: string[];
  hasExistingInvoiceAmount: boolean;
  existingInvoiceAmountTotal: number;
  isCompact: boolean;
  expanded: boolean;
  onToggle: () => void;
  showWCBLicenseEditor?: boolean;
  wcbLicenseInputValue?: string;
  wcbLicenseInputError?: string;
  wcbLicenseInputMinLength?: number;
  wcbLicenseInputMaxLength?: number;
  wcbLicenseNumericOnly?: boolean;
  onWCBLicenseInputChange: (value: string) => void;
  onSaveWCBLicense: () => void;
  isSavingWCBLicense?: boolean;
}

const getPatientName = (claim: Claim | null) => {
  if (!claim?.patient) return 'Unknown patient';
  return (
    formatPatientName(claim.patient.firstName, claim.patient.lastName) ||
    'Unknown patient'
  );
};

const getProviderName = (claim: Claim | null) => {
  if (!claim?.provider) return 'Unknown provider';
  const fullName = `${claim.provider.firstName || ''} ${
    claim.provider.lastName || ''
  }`.trim();
  return fullName || 'Unknown provider';
};

const SummaryRow = ({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) => (
  <Flex justify="space-between" gap="sm" wrap="nowrap">
    <Text size="sm" c="dimmed">
      {label}
    </Text>
    <Text size="sm" fw={500} ta="right">
      {value ?? 'N/A'}
    </Text>
  </Flex>
);

const DetailBlock = ({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) => (
  <Stack gap={4}>
    <Text size="sm" c="dimmed">
      {label}
    </Text>
    <Text size="sm" fw={500}>
      {value?.trim() || 'N/A'}
    </Text>
  </Stack>
);

const formatServiceTime = (
  serviceStart?: string | null,
  serviceEnd?: string | null
) => {
  if (!serviceStart) return 'N/A';

  const start = parseDateTime(serviceStart);
  if (!start.isValid) return 'N/A';

  const startTime = start.toFormat('hh:mm a');
  if (!serviceEnd) return startTime;

  const end = parseDateTime(serviceEnd);
  if (!end.isValid) return startTime;

  const endTime = end.toFormat('hh:mm a');
  return startTime === endTime ? startTime : `${startTime} - ${endTime}`;
};

const getServiceLocationLabel = (serviceLocationIndicator?: string | null) => {
  switch (serviceLocationIndicator?.trim()) {
    case 'C':
      return 'Clinic';
    case 'O':
      return 'Out-Patient';
    case 'E':
      return 'Emergency Dept';
    default:
      return 'N/A';
  }
};

export const ClaimInvoicePreview = ({
  claim,
  selectedTariffIDs,
  hasExistingInvoiceAmount,
  existingInvoiceAmountTotal,
  isCompact,
  expanded,
  onToggle,
  showWCBLicenseEditor = false,
  wcbLicenseInputValue = '',
  wcbLicenseInputError,
  wcbLicenseInputMinLength,
  wcbLicenseInputMaxLength,
  wcbLicenseNumericOnly = false,
  onWCBLicenseInputChange,
  onSaveWCBLicense,
  isSavingWCBLicense = false,
}: ClaimInvoicePreviewProps) => {
  const { translate } = useTranslation();
  const computedColorScheme = useComputedColorScheme('dark');

  const title = translate(
    'pages:claims.invoiceModal.claimSummaryTitle',
    'Claim Information'
  );
  const diagnosisValue = Array.from(
    new Set([
      claim?.icd9,
      ...(claim?.tariffs || []).map((tariff) => tariff.icd9),
    ])
  )
    .filter((value): value is string => Boolean(value?.trim()))
    .join(', ');
  const hospitalValue =
    getHospitalNameForCode(claim?.hospitalCode) ||
    claim?.hospitalName ||
    claim?.hospitalCode ||
    'N/A';
  const selectedTariffs = selectedTariffIDs.length
    ? (claim?.tariffs || []).filter((tariff) =>
        selectedTariffIDs.includes(tariff.id)
      )
    : claim?.tariffs || [];

  const content = (
    <Stack gap="md">
      <Card withBorder radius="md" padding="md">
        <Stack gap="xs">
          <Text fw={600}>
            {translate(
              'pages:claims.invoiceModal.patientInformationTitle',
              'Patient'
            )}
          </Text>
          <SummaryRow
            label={translate(
              'pages:claims.invoiceModal.claimNumberLabel',
              'Claim #'
            )}
            value={claim?.claimNumber || ''}
          />
          <SummaryRow
            label={translate(
              'pages:claims.invoiceModal.healthcardLabel',
              'Healthcard'
            )}
            value={claim?.healthcard?.number || ''}
          />
          <SummaryRow
            label={translate(
              'pages:claims.invoiceModal.serviceDateLabel',
              'Service Date'
            )}
            value={formatDateTime(claim?.serviceStart, { fallback: 'N/A' })}
          />
          <SummaryRow
            label={translate(
              'pages:claims.invoiceModal.serviceTimeLabel',
              'Service Time'
            )}
            value={formatServiceTime(claim?.serviceStart, claim?.serviceEnd)}
          />
          <SummaryRow
            label={translate(
              'pages:claims.invoiceModal.diagnosisLabel',
              'Diagnosis'
            )}
            value={diagnosisValue || 'N/A'}
          />
          <SummaryRow
            label={translate(
              'pages:claims.invoiceModal.patientNameLabel',
              'Patient Name'
            )}
            value={getPatientName(claim)}
          />
        </Stack>
      </Card>

      <Card withBorder radius="md" padding="md">
        <Stack gap="xs">
          <Text fw={600}>
            {translate(
              'pages:claims.invoiceModal.billingInformationTitle',
              'Billing'
            )}
          </Text>
          <Divider />
          <SummaryRow
            label={translate(
              'pages:claims.invoiceModal.providerLabel',
              'Provider'
            )}
            value={getProviderName(claim)}
          />
          {showWCBLicenseEditor ? (
            <Stack gap="xs">
              <Alert color="yellow" variant="light">
                <Flex gap={'sm'} direction={'row'} align={'center'}>
                  <IconAlertTriangle size={16} />
                  {translate(
                    'pages:claims.invoiceModal.errors.invalidWCBBillingNumber',
                    'Provider is missing a valid WCB billing number for the selected site.'
                  )}
                </Flex>
              </Alert>
              <Flex gap="sm" align="end">
                <TextInput
                  label={translate(
                    'pages:claims.invoiceModal.wcbBillingNumberLabel',
                    'WCB Billing Number'
                  )}
                  placeholder={translate(
                    'pages:claims.invoiceModal.wcbBillingNumberPlaceholder',
                    'Enter WCB billing number'
                  )}
                  value={wcbLicenseInputValue}
                  error={wcbLicenseInputError}
                  minLength={wcbLicenseInputMinLength}
                  maxLength={wcbLicenseInputMaxLength}
                  inputMode={wcbLicenseNumericOnly ? 'numeric' : undefined}
                  onChange={(event) =>
                    onWCBLicenseInputChange(event.currentTarget.value)
                  }
                  style={{ flex: 1 }}
                />
                <Button
                  onClick={() => onSaveWCBLicense()}
                  loading={isSavingWCBLicense}
                  disabled={
                    !wcbLicenseInputValue.trim() ||
                    !!wcbLicenseInputError ||
                    isSavingWCBLicense
                  }
                  c={computedColorScheme === 'dark' ? 'white' : 'black'}
                >
                  {translate(
                    'pages:claims.invoiceModal.saveWCBBillingNumber',
                    'Save'
                  )}
                </Button>
              </Flex>
            </Stack>
          ) : null}
          <SummaryRow
            label={translate('pages:claims.invoiceModal.payorLabel', 'Payor')}
            value={claim?.payor?.name || ''}
          />
          <SummaryRow
            label={translate('pages:claims.invoiceModal.siteLabel', 'Site')}
            value={claim?.site?.name || ''}
          />
          <SummaryRow
            label={translate(
              'pages:claims.invoiceModal.hospitalLabel',
              'Hospital'
            )}
            value={hospitalValue || ''}
          />
          <SummaryRow
            label={translate(
              'pages:claims.invoiceModal.serviceLocationDetailLabel',
              'Service location'
            )}
            value={
              getServiceLocationLabel(claim?.serviceLocationIndicator) || ''
            }
          />
          {hasExistingInvoiceAmount ? (
            <SummaryRow
              label={translate(
                'pages:claims.invoiceModal.existingInvoiceTotalLabel',
                'Existing invoice total'
              )}
              value={`$${existingInvoiceAmountTotal.toFixed(2)}`}
            />
          ) : null}
        </Stack>
      </Card>

      <Card withBorder radius="md" padding="md">
        <Stack gap="sm">
          <Text fw={600}>
            {translate(
              'pages:claims.invoiceModal.additionalClaimDetailsTitle',
              'Additional Claim Details'
            )}
          </Text>
          <Divider />
          <DetailBlock
            label={translate(
              'pages:claims.invoiceModal.internalCommentsLabel',
              'Internal comments'
            )}
            value={claim?.internalComments}
          />
          <DetailBlock
            label={translate(
              'pages:claims.invoiceModal.providerCommentsLabel',
              'Provider comments'
            )}
            value={claim?.providerComments}
          />
          <DetailBlock
            label={translate(
              'pages:claims.invoiceModal.providerNotesLabel',
              'Provider notes'
            )}
            value={claim?.providerNotes}
          />
          <DetailBlock
            label={translate(
              'pages:claims.invoiceModal.mhscCommentsLabel',
              'MHSC comments'
            )}
            value={claim?.clerkComments}
          />
        </Stack>
      </Card>

      <Card withBorder radius="md" padding="md">
        <Stack gap="xs">
          <Text fw={600}>
            {translate('pages:claims.invoiceModal.tariffsTitle', 'Tariffs')}
          </Text>
          <Divider />
          {selectedTariffs.length ? (
            selectedTariffs.map((tariff) => (
              <Flex key={tariff.id} justify="space-between" gap="sm">
                <Text size="sm">
                  {`${tariff.prefix}${tariff.code} x${tariff.units || 1}`}
                </Text>
                <Text size="sm" fw={500}>
                  ${tariff.total.toFixed(2)}
                </Text>
              </Flex>
            ))
          ) : (
            <Text size="sm" c="dimmed">
              {translate(
                'pages:claims.invoiceModal.noTariffsSelected',
                'No tariffs selected'
              )}
            </Text>
          )}
        </Stack>
      </Card>
    </Stack>
  );

  if (isCompact) {
    return (
      <Accordion
        value={expanded ? 'claim-info' : null}
        onChange={() => onToggle()}
        variant="separated"
        radius="md"
      >
        <Accordion.Item value="claim-info">
          <Accordion.Control>{title}</Accordion.Control>
          <Accordion.Panel>{content}</Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    );
  }

  return (
    <Card
      withBorder
      radius="md"
      padding="md"
      bg="transparent"
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
      }}
    >
      <Text fw={700}>{title}</Text>
      <Divider my="sm" />
      {content}
    </Card>
  );
};
